import { useState } from "react";
import { describeError } from "../../api/client";
import { Combobox, type ComboboxOption } from "../../components/Combobox";
import { Field } from "../../components/Field";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { invalidateExact } from "../../state/queryClient";
import { useQuery } from "../../state/useQuery";
import { createOrGetShow, getShowOptions } from "./api";

/**
 * Combobox options for the dropdown mode. Codex round 6 (PR #386): the options
 * list is a bounded window; a value (e.g. a bookmarked `showId`) outside it
 * would otherwise silently render as "Todos los shows", so it keeps a
 * disabled placeholder entry.
 */
function showOptions(
  data: { showId: string; nombre: string }[],
  value: string,
  allowEmpty: boolean,
): ComboboxOption[] {
  return [
    ...(allowEmpty ? [{ value: "", label: "Todos los shows" }] : []),
    ...(value !== "" && !data.some((option) => option.showId === value)
      ? [{ value, label: "Show seleccionado (fuera de esta lista)", disabled: true }]
      : []),
    ...data.map((option) => ({ value: option.showId, label: option.nombre })),
  ];
}

/**
 * Recognizable-by-name Show selector for the contact-import panel (epic
 * #236 tarea #238, work unit 4) — same "options" pattern as
 * `TemplateSelector`/`SegmentSelector`: the operator picks a Show by name,
 * never by typing its ID, and every fresh import starts with no Show
 * selected (unlike a campaign editor reopening an existing draft, there is
 * no "currently assigned Show that might fall outside the bounded list" case
 * to handle here).
 *
 * `allowEmpty` (epic #236 tarea #239, work unit 239.6): the audience-filter
 * editor treats Show as one of 4 OPTIONAL combinable criteria, unlike the
 * contact-import flow where a Show is mandatory. Defaults to `false` so the
 * only existing consumer (`ContactImportPanel`) keeps its exact current
 * behavior (required select, no empty option) with zero changes on its end.
 *
 * `allowCreate` (feat/show-create-on-import): lets the operator type a Show
 * name that doesn't exist yet and create it inline (get-or-create, `POST
 * panel/shows`) instead of requiring it to already exist. Defaults to
 * `false` -- only `ContactImportPanel` opts in; `AudienceFilterEditor`
 * filters campaigns by an EXISTING Show, so creating one there makes no
 * sense and must keep its exact current behavior.
 *
 * `renderAs` (REDISENO-PANEL.md §7.3, panel-redesign-v3 T7): the import
 * wizard's step 1 renders the Show options as "large radio options" (2px
 * blue border on the selected one) instead of a `<select>`. Defaults to
 * `"select"` (a searchable `Combobox` since panel feedback 2026-10-30) so every existing consumer (the Contacts list filter,
 * `AudienceFilterEditor`) keeps its exact current markup; only
 * `ContactImportPanel`'s step 1 opts into `"radio"`. The create-toggle/
 * create-form flow below is shared by both rendering modes unchanged.
 */
export function ShowSelector({
  id,
  value,
  onChange,
  disabled,
  allowEmpty = false,
  allowCreate = false,
  renderAs = "select",
  hideLabel = false,
}: {
  id: string;
  value: string;
  onChange: (showId: string) => void;
  disabled?: boolean;
  allowEmpty?: boolean;
  allowCreate?: boolean;
  renderAs?: "select" | "radio";
  /** Visually hides the "Show" label (toolbar context); it stays the control's accessible name. */
  hideLabel?: boolean;
}) {
  const { data, error, loading, refetch } = useQuery({
    key: ["show-options"],
    queryFn: () => getShowOptions(),
  });

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [createError, setCreateError] = useState<unknown>(undefined);
  const [submitting, setSubmitting] = useState(false);

  function openCreateForm() {
    setCreating(true);
    setCreateError(undefined);
  }

  function closeCreateForm() {
    setCreating(false);
    setNewName("");
    setCreateError(undefined);
  }

  async function handleCreate(event: React.FormEvent) {
    event.preventDefault();
    const nombre = newName.trim();
    if (!nombre || submitting) return;

    setSubmitting(true);
    setCreateError(undefined);
    try {
      const show = await createOrGetShow(nombre);
      onChange(show.showId);
      // Codex finding (P2, revision automatica sobre PR #284): `refetch()`
      // only updates THIS `useQuery` instance's own local state -- any other
      // mounted consumer of the same "show-options" key (e.g. a second
      // <ShowSelector> elsewhere on the page, or ContactsScreen's own lookup
      // for the active-filter chip's label) never learns about the newly
      // created show until it remounts. `invalidateExact` drops the shared
      // cache entry and notifies EVERY subscriber (queryClient.ts) -- this
      // instance's own `useQuery(["show-options"])` included, since it is
      // registered as a subscriber too.
      //
      // Codex finding (P2, revision automatica sobre PR #285): an explicit
      // `await refetch()` right after `invalidateExact` used to force THIS
      // instance's own fetch, which aborted-and-superseded the single shared
      // fetch every OTHER notified subscriber had just deduped onto --
      // reintroducing the exact "other consumers left stuck" bug this fix
      // exists to close, just one call later. Dropped entirely: this
      // instance is already one of the subscribers `invalidateExact` wakes
      // up, so it gets the same fresh data through that one shared fetch,
      // same as everyone else -- no separate forced refetch needed.
      invalidateExact([["show-options"]]);
      closeCreateForm();
    } catch (error) {
      setCreateError(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="v2-show-selector">
      {loading && <p role="status">Cargando shows…</p>}

      {!data && error !== undefined && (
        <FeedbackPanel variant="error" message={describeError(error, "No se pudieron cargar los shows.")} onRetry={refetch} />
      )}

      {data && (
        <>
          {data.length === 0 ? (
            <>
              <span className="v2-show-selector__label">Show</span>
              <FeedbackPanel variant="empty" message="No hay shows disponibles." />
            </>
          ) : renderAs === "radio" ? (
            <div className="v2-show-selector__radio-group" role="radiogroup" aria-label="Show">
              {data.map((option) => (
                <label
                  key={option.showId}
                  className={
                    option.showId === value
                      ? "v2-show-selector__radio-option v2-show-selector__radio-option--selected"
                      : "v2-show-selector__radio-option"
                  }
                >
                  <input
                    type="radio"
                    name={id}
                    value={option.showId}
                    checked={option.showId === value}
                    disabled={disabled}
                    onChange={() => onChange(option.showId)}
                  />
                  {option.nombre}
                </label>
              ))}
            </div>
          ) : (
            <Field label="Show" htmlFor={id} hideLabel={hideLabel}>
              <Combobox
                id={id}
                value={value}
                disabled={disabled}
                required={!allowEmpty}
                onChange={onChange}
                options={showOptions(data, value, allowEmpty)}
                placeholder="Selecciona un show"
              />
            </Field>
          )}
        </>
      )}

      {allowCreate && data && !creating && (
        <button
          type="button"
          className="v2-show-selector__create-toggle"
          onClick={openCreateForm}
          disabled={disabled}
        >
          + Crear un show nuevo
        </button>
      )}

      {allowCreate && data && creating && (
        <form className="v2-show-selector__create-form" onSubmit={handleCreate}>
          <Field label="Nombre del show nuevo" htmlFor={`${id}-new-nombre`}>
            <input
              id={`${id}-new-nombre`}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              disabled={submitting}
            />
          </Field>
          <div className="v2-show-selector__create-actions">
            <button
              type="submit"
              className="v2-u-target-min"
              disabled={submitting || !newName.trim()}
            >
              {submitting ? "Creando…" : "Crear"}
            </button>
            <button
              type="button"
              className="v2-u-target-min v2-show-selector__cancel"
              onClick={closeCreateForm}
              disabled={submitting}
            >
              Cancelar
            </button>
          </div>
          {createError !== undefined && (
            <FeedbackPanel variant="error" message={describeError(createError, "No se pudo crear el show.")} />
          )}
        </form>
      )}
    </div>
  );
}
